import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import ReasonSelect from "../../../../components/inputs/ReasonSelect";
import SvgMathLabel from "../../../../components/diagrams/SvgMathLabel";

const TYPE = "MaturaAcuteTriangleAngleProofOpenTask";

const STEP_ONE = [
  {
    value: "dbeIsosceles",
    label: <><InlineMath math="|DB|=|DE|" /> oznacza, że trójkąt <InlineMath math="DBE" /> jest równoramienny, więc <InlineMath math={String.raw`\angle BED=\angle DBE=\beta`} />.</>,
  },
  {
    value: "medianBisectsAngle",
    label: <>Środkowa <InlineMath math="BE" /> dzieli kąt <InlineMath math="ABC" /> na dwie równe części.</>,
  },
  {
    value: "equalSegmentsRightAngle",
    label: <>Z równości <InlineMath math="|DB|=|DE|" /> wynika, że kąt <InlineMath math="BDE" /> jest prosty.</>,
  },
  {
    value: "dbeEquilateral",
    label: <>Równość <InlineMath math="|DB|=|DE|" /> oznacza, że trójkąt <InlineMath math="DBE" /> jest równoboczny.</>,
  },
];

const STEP_TWO = [
  {
    value: "hypotenuseMidpoint",
    label: <>Punkt <InlineMath math="E" /> jest środkiem przeciwprostokątnej <InlineMath math="AC" /> trójkąta prostokątnego <InlineMath math="ADC" />, zatem <InlineMath math="|AE|=|DE|=|CE|" /> i <InlineMath math={String.raw`\angle ADE=\angle EAD=\alpha`} />.</>,
  },
  {
    value: "medianPerpendicular",
    label: <>Każda środkowa jest prostopadła do boku, który dzieli na połowy, więc <InlineMath math={String.raw`BE\perp AC`} />.</>,
  },
  {
    value: "altitudeBisectsSide",
    label: <>Wysokość <InlineMath math="CD" /> dzieli bok <InlineMath math="AB" /> na połowy, więc <InlineMath math="|AD|=|DB|" />.</>,
  },
  {
    value: "midpointEqualLegs",
    label: <>Skoro <InlineMath math="E" /> jest środkiem <InlineMath math="AC" />, to <InlineMath math="|DE|=|DB|" /> bez korzystania z pozostałych danych.</>,
  },
];

const STEP_THREE = [
  {
    value: "supplementary",
    label: <>Kąty <InlineMath math={String.raw`\angle ADE`} /> i <InlineMath math={String.raw`\angle EDB`} /> są przyległe, więc <InlineMath math={String.raw`\alpha+(180^\circ-2\beta)=180^\circ`} />, skąd <InlineMath math={String.raw`\alpha=2\beta`} />.</>,
  },
  {
    value: "exteriorAngle",
    label: <>Z twierdzenia o kącie zewnętrznym w trójkącie <InlineMath math="DBE" /> otrzymujemy <InlineMath math={String.raw`\angle ADE=\angle DBE+\angle BED=2\beta`} />, a więc <InlineMath math={String.raw`\alpha=2\beta`} />.</>,
  },
  {
    value: "angleSum",
    label: <>Suma kątów w trójkącie <InlineMath math="ABC" /> daje bezpośrednio <InlineMath math={String.raw`\alpha=\beta`} />.</>,
  },
  {
    value: "verticalAngles",
    label: <>Kąty <InlineMath math={String.raw`\angle CAB`} /> i <InlineMath math={String.raw`\angle ABE`} /> są wierzchołkowe, więc mają równe miary.</>,
  },
];

function shuffle(items) {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(Math.random() * (index + 1));
    [result[index], result[target]] = [result[target], result[index]];
  }
  return result;
}

function orderedChoices(choices, order) {
  const byValue = Object.fromEntries(choices.map((choice) => [choice.value, choice]));
  return order.map((value) => byValue[value]).filter(Boolean);
}

function choiceLabel(choices, value) {
  return choices.find((choice) => choice.value === value)?.label || "--";
}

export function generateTask(options = {}) {
  return {
    type: TYPE,
    data: {
      maxPoints: 2,
      stepOneOrder: shuffle(STEP_ONE.map(({ value }) => value)),
      stepTwoOrder: shuffle(STEP_TWO.map(({ value }) => value)),
      stepThreeOrder: shuffle(STEP_THREE.map(({ value }) => value)),
    },
    source: {
      variant: options.variant === "original" ? "original" : "random",
      exam: "MATURA",
      session: options.session || "2026_CZE",
      taskNumber: "20",
      basedOn: "MMAP-P0-100-A-2606-20",
      originSession: "2026_CZE",
      originTaskNumber: "20",
    },
    tags: ["matura", "dowod", "geometria", "katy", "trojkat-rownoramienny"],
  };
}

function ProofDiagram() {
  return (
    <svg
      viewBox="0 0 520 360"
      role="img"
      aria-label="Trójkąt ostrokątny ABC ze środkową BE, wysokością CD i odcinkiem DE"
      style={{ width: "min(100%, 520px)", margin: "10px auto", display: "block" }}
    >
      <path d="M70 300 L474 300 L300 40 Z" fill="none" stroke="currentColor" strokeWidth="3" />
      <path d="M300 40 L300 300" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="7 6" opacity="0.8" />
      <path d="M185 170 L474 300" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="7 6" opacity="0.8" />
      <path d="M185 170 L300 300 L474 300" fill="none" stroke="#8b5cf6" strokeWidth="4" />
      <path d="M300 283 A17 17 0 0 1 317 300" fill="none" stroke="#ff9f1a" strokeWidth="3" />
      <path d="M76 300 A42 42 0 0 1 91 267" fill="none" stroke="#8b5cf6" strokeWidth="3" />
      <path d="M432 300 A42 42 0 0 1 449 270" fill="none" stroke="#8b5cf6" strokeWidth="3" />
      {[["A",48,318],["B",480,318],["C",300,21],["D",290,321],["E",158,158]].map(([name,x,y]) => <SvgMathLabel key={name} x={x} y={y} math={name} align="left" fontSize={24} height={32}/>)}
      <SvgMathLabel x={115} y={282} math="\alpha" align="left" fontSize={22} color="#8b5cf6"/>
      <SvgMathLabel x={390} y={283} math="\beta" align="left" fontSize={22} color="#8b5cf6"/>
    </svg>
  );
}

export function renderTask() {
  return (
    <div>
      <p>W trójkącie ostrokątnym <InlineMath math="ABC" /> punkt <InlineMath math="D" /> leży na boku <InlineMath math="AB" />, a punkt <InlineMath math="E" /> leży na boku <InlineMath math="AC" />.</p>
      <p>Odcinek <InlineMath math="BE" /> jest środkową trójkąta <InlineMath math="ABC" />, a odcinek <InlineMath math="CD" /> jest wysokością tego trójkąta. Ponadto <InlineMath math="|DB|=|DE|" />.</p>
      <ProofDiagram />
      <p>Wykaż, że</p>
      <BlockMath math={String.raw`|\angle CAB|=2\cdot|\angle ABE|.`} />
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const setStepOne = (stepOne) => onChange?.({ stepOne, stepTwo: "", stepThree: "" });
  const setStepTwo = (stepTwo) => onChange?.({ ...userAnswer, stepTwo, stepThree: "" });
  return (
    <div style={{ display: "grid", gap: 18, marginTop: 12, textAlign: "left" }}>
      <div style={{ display: "grid", gap: 8 }}>
        <div><strong>Krok 1.</strong> Wyprowadź zależność kątów w trójkącie <InlineMath math="DBE" />.</div>
        <ReasonSelect choices={orderedChoices(STEP_ONE, data.stepOneOrder)} value={userAnswer.stepOne || ""} onChange={setStepOne} disabled={disabled} placeholder="Wybierz zależność" ariaLabel="Zależność w trójkącie DBE" />
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        <div><strong>Krok 2.</strong> Wykorzystaj środkową <InlineMath math="BE" /> i wysokość <InlineMath math="CD" />.</div>
        <ReasonSelect choices={orderedChoices(STEP_TWO, data.stepTwoOrder)} value={userAnswer.stepTwo || ""} onChange={setStepTwo} disabled={disabled || !userAnswer.stepOne} placeholder="Wybierz przesłankę" disabledLabel="Najpierw uzupełnij krok 1" ariaLabel="Własność środka przeciwprostokątnej" />
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        <div><strong>Krok 3.</strong> Połącz otrzymane zależności.</div>
        <ReasonSelect choices={orderedChoices(STEP_THREE, data.stepThreeOrder)} value={userAnswer.stepThree || ""} onChange={(stepThree) => onChange?.({ ...userAnswer, stepThree })} disabled={disabled || !userAnswer.stepTwo} placeholder="Wybierz domknięcie dowodu" disabledLabel="Najpierw uzupełnij krok 2" ariaLabel="Domknięcie dowodu" />
      </div>
    </div>
  );
}

export function checkAnswer(task, value = {}) {
  const stepOne = value.stepOne === "dbeIsosceles";
  const stepTwo = value.stepTwo === "hypotenuseMidpoint";
  const stepThree = ["supplementary", "exteriorAngle"].includes(value.stepThree);
  const complete = stepOne && stepTwo && stepThree;
  return {
    correct: complete,
    score: complete ? 2 : Number(stepOne || stepTwo),
    maxScore: 2,
    details: { stepOne, stepTwo, stepThree },
  };
}

export function renderAnswer() {
  return <><InlineMath math={String.raw`\angle BED=\angle DBE=\beta`} />, <InlineMath math={String.raw`\angle ADE=\angle EAD=\alpha`} />, zatem <InlineMath math={String.raw`\alpha=2\beta`} />.</>;
}

export function renderUserAnswer(value = {}) {
  if (!value.stepOne && !value.stepTwo && !value.stepThree) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <span><strong>Krok 1:</strong> {choiceLabel(STEP_ONE, value.stepOne)}</span>
      <span><strong>Krok 2:</strong> {choiceLabel(STEP_TWO, value.stepTwo)}</span>
      <span><strong>Krok 3:</strong> {choiceLabel(STEP_THREE, value.stepThree)}</span>
    </div>
  );
}

export function renderExplanation() {
  return (
    <div>
      <p>Oznaczmy <InlineMath math={String.raw`|\angle CAB|=\alpha`} /> oraz <InlineMath math={String.raw`|\angle ABE|=\beta`} />.</p>
      <p><strong>Krok 1.</strong> Z warunku <InlineMath math="|DB|=|DE|" /> wynika, że trójkąt <InlineMath math="DBE" /> jest równoramienny.</p>
      <BlockMath math={String.raw`\angle BED=\angle DBE=\beta,\qquad \angle EDB=180^\circ-2\beta.`} />
      <p><strong>Krok 2.</strong> Odcinek <InlineMath math="BE" /> jest środkową, więc <InlineMath math="E" /> jest środkiem odcinka <InlineMath math="AC" />. Ponieważ <InlineMath math="CD" /> jest wysokością, trójkąt <InlineMath math="ADC" /> jest prostokątny, a <InlineMath math="AC" /> jest jego przeciwprostokątną.</p>
      <p>Środek przeciwprostokątnej jest równoodległy od wszystkich wierzchołków trójkąta prostokątnego, dlatego</p>
      <BlockMath math={String.raw`|AE|=|DE|=|CE|.`} />
      <p>Trójkąt <InlineMath math="ADE" /> jest równoramienny, zatem</p>
      <BlockMath math={String.raw`\angle ADE=\angle EAD=\angle CAB=\alpha.`} />
      <p><strong>Krok 3.</strong> Kąty <InlineMath math={String.raw`\angle ADE`} /> i <InlineMath math={String.raw`\angle EDB`} /> są przyległe.</p>
      <BlockMath math={String.raw`\alpha+(180^\circ-2\beta)=180^\circ,\qquad \alpha=2\beta.`} />
      <p><strong>Wniosek.</strong> <InlineMath math={String.raw`|\angle CAB|=2\cdot|\angle ABE|`} />, co należało wykazać.</p>
    </div>
  );
}

export const getCorrectPath = () => ({
  stepOne: "dbeIsosceles",
  stepTwo: "hypotenuseMidpoint",
  stepThree: "supplementary",
});
